import { FC, ReactNode, useState } from 'react'
import * as Yup from 'yup'
import { Col, Select, Divider } from 'antd'
import { Formik } from 'formik'
import { useTranslation } from 'react-i18next'
import { useDateTime } from '../../../shared/useDateTime'
import { PerModal } from '../../../components/custom_components/Modal'
import { FormikSelect, FormItem, FormikDatePicker, InputNumber, Input } from '../../../components/formik-component'
import { availableCountries } from './filterModalUtil'
import { UserInfo, Currency, Country } from '../../../types'
import PerText from '../../../components/custom_components/Text'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import { EmployeeModalFormDataType, NO_OFFICE_FILTER_VALUE } from '../util'

const { Option } = Select
export type CurrencyAndCountry = {
  currency: Currency
  country: Country
}
type EmployeeFilterModalProps = {
  filterModalData: CurrencyAndCountry[]
  hasResultScore: boolean
  modalOpen: boolean
  user: UserInfo
  modalFormValues: EmployeeModalFormDataType
  onSubmit: (values: EmployeeModalFormDataType) => void
  onCancel: () => void
  showOfficeFilter?: boolean
  possibleOffices?: string[]
}

type rowContent = {
  label?: string
  input: ReactNode
  showOnlyWhen?: boolean
}

type FilterRowProps = {
  left?: rowContent
  right?: rowContent
  to?: boolean
  margin?: boolean
}

const FilterRow: FC<FilterRowProps> = ({ left, right, to, margin }) => {
  const colPadding = (side) => (!side || (side && side.showOnlyWhen === false) ? '6px' : undefined)
  const renderContent = (side: rowContent) => (
    <>
      {(side.showOnlyWhen === undefined ? true : side.showOnlyWhen) && (
        <Col span={to ? 11 : 12} offset={!left ? 12 : 0}>
          <div style={{ paddingLeft: colPadding(left), paddingRight: colPadding(right) }}>
            <PerText appearance="label-small" color={side.label ? 'secondary' : 'transparent'} mb="xxxs">
              {side.label ? side.label : '.'}
            </PerText>
            {side.input}
          </div>
        </Col>
      )}
    </>
  )
  return (
    <PerFlexRow mb={margin ? 'xs' : undefined} align="middle">
      {left && renderContent(left)}
      {to && (
        <Col span={2}>
          <PerText mt="m" align="center">
            -
          </PerText>
        </Col>
      )}
      {right && renderContent(right)}
    </PerFlexRow>
  )
}

export const EmployeeFilterModal: FC<EmployeeFilterModalProps> = ({
  filterModalData,
  hasResultScore,
  modalOpen,
  onSubmit,
  onCancel,
  user,
  modalFormValues,
  showOfficeFilter = false,
  possibleOffices = [],
}) => {
  const [t] = useTranslation()
  const { compareDates } = useDateTime(user)
  const availableCurrencies = [
    ...new Set(
      filterModalData.map((el) => {
        return el.currency
      }),
    ),
  ]
  const [isSubmitting, toggleSubmitting] = useState<boolean>(false)

  const sortEmployeeCountriesList = (countries: Country[]): Country[] => [...countries].sort()

  const employeeCountries = availableCountries(filterModalData)
  const sortedEmployeeCountries = sortEmployeeCountriesList(employeeCountries)

  return (
    <Formik
      initialValues={modalFormValues}
      enableReinitialize={true}
      onSubmit={(values: EmployeeModalFormDataType) => {
        toggleSubmitting(true)
        onSubmit(values)
        toggleSubmitting(false)
      }}
      validateOnBlur={false}
      validateOnChange={true}
      validationSchema={() => {
        return Yup.lazy((values: EmployeeModalFormDataType) => {
          return Yup.object().shape({
            startDate: Yup.object().shape({
              dateBefore: Yup.mixed().when('startDate.dateAfter', (_startDate: string, schema: Yup.MixedSchema) => {
                return schema.test({
                  // unique name identifying the test
                  name: 'start_date_after',
                  // test function, determines schema validity
                  test: (value: string) => {
                    if (values.startDate.dateAfter) {
                      return compareDates(values.startDate.dateAfter, value) <= 0
                    }
                    return true
                  },
                  // the validation error message
                  message: t('formError.dateBefore', { date: t('employeeList.filterModal.startDate.dateAfter') }) as string,
                })
              }),
            }),
            endDate: Yup.object().shape({
              dateBefore: Yup.mixed().when('endDate.dateAfter', (_endDate: string, schema: Yup.MixedSchema) => {
                return schema.test({
                  // unique name identifying the test
                  name: 'end_date_after',
                  // test function, determines schema validity
                  test: (value: string) => {
                    if (values.endDate.dateAfter) {
                      return compareDates(values.endDate.dateAfter, value) <= 0
                    }
                    return true
                  },
                  // the validation error message
                  message: t('formError.dateBefore', { date: t('employeeList.filterModal.endDate.dateAfter') }) as string,
                })
              }),
            }),
            salary: Yup.object().shape({
              upperLimit: Yup.mixed().when('salary.lowerLimit', (_lowerLimit: string, schema: Yup.MixedSchema) => {
                return schema.test({
                  // unique name identifying the test
                  name: 'salary_upperLimit',
                  // test function, determines schema validity
                  test: (value: number) => {
                    if (values.salary.lowerLimit !== undefined && values.salary.upperLimit !== undefined) {
                      return values.salary.lowerLimit < value
                    }
                    return true
                  },
                  // the validation error message
                  message: t('formError.minValue', {
                    fieldName: t('employeeList.filterModal.annualSalaryUpperLimitPlaceholder'),
                    minAmount: `${values.salary.lowerLimit}`,
                    currency: values.currency || '',
                  }) as string,
                })
              }),
            }),
            targetBonus: Yup.object().shape({
              upperLimit: Yup.mixed().when('targetBonus.lowerLimit', (_lowerLimit: string, schema: Yup.MixedSchema) => {
                return schema.test({
                  // unique name identifying the test
                  name: 'targetBonus_upperLimit',
                  // test function, determines schema validity
                  test: (value: number) => {
                    if (values.targetBonus.lowerLimit !== undefined && values.targetBonus.upperLimit !== undefined) {
                      return values.targetBonus.lowerLimit < value
                    }
                    return true
                  },
                  // the validation error message
                  message: t('formError.minValue', {
                    fieldName: t('employeeList.filterModal.targetBonusUpperLimitPlaceholder'),
                    minAmount: `${values.targetBonus.lowerLimit}`,
                    currency: values.currency || '',
                  }) as string,
                })
              }),
            }),
          })
        })
      }}
    >
      {(props) => {
        const { handleSubmit, errors } = props
        const isDisabled = !(Object.keys(errors).length === 0 && errors.constructor === Object)

        return (
          <PerModal
            title={{ heading: t('employeeList.filterModal.title') }}
            centered
            visible={modalOpen}
            onCancel={() => {
              onCancel()
            }}
            footer={{
              formikForm: {
                onSubmit: handleSubmit,
              },
              cancelBtn: {
                onClick: () => {
                  onCancel()
                },
              },
              customBtns: [
                {
                  text: t('global.button.applyFilter'),
                  disabled: isDisabled,
                  color: 'success',
                  icon: 'check_stroke',
                  type: 'submit',
                  eventHandler: { onMouseDown: handleSubmit },
                  isProcessing: { active: isSubmitting },
                },
              ],
            }}
          >
            <FilterRow
              margin={showOfficeFilter}
              left={{
                label: t('employeeList.filterModal.name'),
                input: (
                  <FormItem name="employeeName">
                    <Input
                      type="text"
                      name="employeeName"
                      onChange={(v) => {
                        const value = v.target.value
                        props.setFieldValue('employeeName', !value ? undefined : value)
                      }}
                      size="small"
                      placeholder={t('employeeList.filterModal.namePlaceholder')}
                    />
                  </FormItem>
                ),
              }}
              right={{
                label: t('employeeList.filterModal.country'),
                input: (
                  <FormikSelect
                    name="country"
                    placeholder={t('employeeList.filterModal.countryPlaceholder')}
                    size="small"
                    style={{ width: '100%' }}
                  >
                    {sortedEmployeeCountries.map((country, index) => (
                      <Option value={country} key={country + index}>
                        {t(`global.country.${country}`)}
                      </Option>
                    ))}
                    <Option value={undefined} key="emptyCountry">
                      -
                    </Option>
                  </FormikSelect>
                ),
              }}
            />

            {showOfficeFilter && (
              <FilterRow
                left={{
                  label: t('employeeList.filterModal.office'),
                  input: (
                    <FormikSelect
                      name="office"
                      placeholder={t('employeeList.filterModal.officePlaceholder')}
                      size="small"
                      style={{ width: '100%' }}
                    >
                      {possibleOffices.map((office) => (
                        <Option value={office} key={office}>
                          {office === NO_OFFICE_FILTER_VALUE ? t('employeeList.table.officeNotAvailable') : office}
                        </Option>
                      ))}
                      <Option value={undefined} key="emptyOffice">
                        -
                      </Option>
                    </FormikSelect>
                  ),
                }}
              />
            )}

            <Divider />
            {availableCurrencies.length > 0 && (
              <>
                <FilterRow
                  margin
                  left={{
                    label: t('employeeList.filterModal.currency'),
                    input: (
                      <FormikSelect
                        name="currency"
                        placeholder={t('employeeList.filterModal.currencyPlaceholder')}
                        size="small"
                        style={{ width: '100%' }}
                        onChange={(value) => {
                          if (value === undefined) {
                            props.setFieldValue('currency', value)
                            props.setFieldValue('targetBonus', { lowerLimit: undefined, upperLimit: undefined })
                            props.setFieldValue('salary', { lowerLimit: undefined, upperLimit: undefined })
                          } else {
                            props.setFieldValue('currency', value)
                          }
                        }}
                      >
                        {availableCurrencies.map((currency, index) => (
                          <Option value={currency} key={`${currency}${index}`}>
                            {currency}
                          </Option>
                        ))}
                        <Option value={undefined} key="emptyCurrency">
                          -
                        </Option>
                      </FormikSelect>
                    ),
                  }}
                />
              </>
            )}

            <>
              <FilterRow
                margin
                left={{
                  label: t('employeeList.filterModal.annualSalary'),
                  input: (
                    <FormItem name="salary.lowerLimit">
                      <InputNumber
                        name="salary.lowerLimit"
                        type="currency"
                        currency={props.values.currency ? props.values.currency : undefined}
                        timezoneAndLocale={user}
                        min={0}
                        size="small"
                        width="100%"
                        disabled={!props.values.currency}
                        placeholder={t('employeeList.filterModal.annualSalaryLowerLimitPlaceholder')}
                      />
                    </FormItem>
                  ),
                }}
                to
                right={{
                  input: (
                    <FormItem name="salary.upperLimit">
                      <InputNumber
                        name="salary.upperLimit"
                        type="currency"
                        currency={props.values.currency ? props.values.currency : undefined}
                        timezoneAndLocale={user}
                        min={0}
                        size="small"
                        width="100%"
                        disabled={!props.values.currency}
                        placeholder={t('employeeList.filterModal.annualSalaryUpperLimitPlaceholder')}
                      />
                    </FormItem>
                  ),
                }}
              />
              <FilterRow
                left={{
                  label: t('employeeList.filterModal.targetBonus'),
                  input: (
                    <FormItem name="targetBonus.lowerLimit">
                      <InputNumber
                        name="targetBonus.lowerLimit"
                        type="currency"
                        currency={props.values.currency ? props.values.currency : undefined}
                        timezoneAndLocale={user}
                        min={0}
                        size="small"
                        width="100%"
                        disabled={!props.values.currency}
                        placeholder={t('employeeList.filterModal.targetBonusLowerLimitPlaceholder')}
                      />
                    </FormItem>
                  ),
                }}
                to
                right={{
                  input: (
                    <FormItem name="targetBonus.upperLimit">
                      <InputNumber
                        name="targetBonus.upperLimit"
                        type="currency"
                        currency={props.values.currency ? props.values.currency : undefined}
                        timezoneAndLocale={user}
                        min={0}
                        size="small"
                        width="100%"
                        disabled={!props.values.currency}
                        placeholder={t('employeeList.filterModal.targetBonusUpperLimitPlaceholder')}
                      />
                    </FormItem>
                  ),
                }}
              />
            </>

            <Divider />

            {hasResultScore && (
              <>
                <FilterRow
                  left={{
                    showOnlyWhen: hasResultScore,
                    label: t('employeeList.filterModal.evaluationScore'),
                    input: (
                      <FormItem name="resultScore">
                        <InputNumber
                          timezoneAndLocale={user}
                          name="resultScore"
                          min={0}
                          max={10}
                          size="small"
                          style={{ width: '100%' }}
                          onChange={(value) => {
                            props.setFieldValue('resultScore', (value && !isNaN(value)) || value === 0 ? value : undefined)
                          }}
                          placeholder={t('employeeList.filterModal.evaluationScorePlaceholder')}
                        />
                      </FormItem>
                    ),
                  }}
                />

                <Divider />
              </>
            )}

            <FilterRow
              margin
              left={{
                label: t('employeeList.filterModal.startDate.dateAfter'),
                input: (
                  <FormItem name="startDate.dateAfter">
                    <FormikDatePicker name="startDate.dateAfter" size="small" allowClear={true} width="100%" timeZoneAndLocale={user} />
                  </FormItem>
                ),
              }}
              right={{
                label: t('employeeList.filterModal.startDate.dateBefore'),
                input: (
                  <FormItem name="startDate.dateBefore">
                    <FormikDatePicker name="startDate.dateBefore" size="small" allowClear={true} width="100%" timeZoneAndLocale={user} />
                  </FormItem>
                ),
              }}
            />
            <FilterRow
              left={{
                label: t('employeeList.filterModal.endDate.dateAfter'),
                input: (
                  <FormItem name="endDate.dateAfter">
                    <FormikDatePicker name="endDate.dateAfter" size="small" allowClear={true} width="100%" timeZoneAndLocale={user} />
                  </FormItem>
                ),
              }}
              right={{
                label: t('employeeList.filterModal.endDate.dateBefore'),
                input: (
                  <FormItem name="endDate.dateBefore">
                    <FormikDatePicker name="endDate.dateBefore" size="small" allowClear={true} width="100%" timeZoneAndLocale={user} />
                  </FormItem>
                ),
              }}
            />
          </PerModal>
        )
      }}
    </Formik>
  )
}
